import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ArchiveEntityOptionView, ReserveEntityOptionView } from '@api-proxies';
import { FiltersFormGroup, FiltersFormValue, ICustomizationFilters } from '@models/customization.types';
import { Condition } from '@models/enums/Condition';
import { AwaiterService } from '@services/awaiter.service';
import { CustomizationService } from '@services/http/clients/customization.service';
import { ReactiveDropdownComponent } from '@shared/reactive-form-controls/reactive-dropdown/reactive-dropdown.component';

@Component({
    selector: 'app-customization-filters',
    templateUrl: './customization-filters.component.html',
    styleUrls: ['./customization-filters.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ReactiveFormsModule, ReactiveDropdownComponent]
})
export class CustomizationFiltersComponent {
    readonly entityTypeId = input<number>();

    filtersChange = output<ICustomizationFilters>();

    filtersForm!: FiltersFormGroup;

    readonly statusOptions = signal<ArchiveEntityOptionView[]>([]);
    readonly sourceOptions = signal<ReserveEntityOptionView[]>([]);

    private readonly isInitialized = signal<boolean>(false);
    private readonly optionsLoaded = signal<boolean>(false);
    private readonly formValueSignal = signal<Partial<FiltersFormValue> | null>(null);
    private readonly previousFilters = signal<ICustomizationFilters | null>(null);

    private readonly awaiter = inject(AwaiterService);
    private readonly CustomizationService = inject(CustomizationService);

    constructor() {
        this.filtersForm = new FormGroup({
            status: new FormControl<ArchiveEntityOptionView | null>(null),
            source: new FormControl<ReserveEntityOptionView | null>(null)
        });

        const formValueSignal = toSignal(this.filtersForm.valueChanges, {
            initialValue: this.filtersForm.value
        });

        effect(() => {
            const formValue = formValueSignal();
            this.formValueSignal.set(formValue);
        });

        effect(() => {
            const formValue = this.formValueSignal();
            if (formValue) {
                this.emitCurrentFilters();
            }
        });

        effect(() => {
            if (!this.optionsLoaded()) {
                this.loadFilterOptions();
            }
        });

        effect(() => {
            const entityTypeId = this.entityTypeId();
            if (entityTypeId && this.isInitialized()) {
                this.resetFilters();
            }
        });
    }

    private resetFilters(): void {
        const defaultStatus = this.statusOptions().find((s) => s.id === Condition.Active);

        this.filtersForm.setValue(
            {
                status: defaultStatus ?? null,
                source: null
            },
            { emitEvent: false }
        );

        this.formValueSignal.set({
            status: defaultStatus ?? null,
            source: null
        });

        const filters: ICustomizationFilters = {
            status: defaultStatus ?? undefined,
            source: undefined
        };

        this.previousFilters.set(filters);
    }

    private async loadFilterOptions(): Promise<void> {
        if (this.optionsLoaded()) return;

        await this.awaiter.awaitAction('Loading filter options', async () => {
            const statusFilterOptions = await this.CustomizationService.getArchiveOptions();
            const sourceFilterOptions = await this.CustomizationService.getReservedOptions();

            this.statusOptions.set(statusFilterOptions || []);
            this.sourceOptions.set(sourceFilterOptions || []);
            this.optionsLoaded.set(true);

            if (statusFilterOptions && !this.isInitialized()) {
                const defaultStatus = statusFilterOptions.find((s) => s.id === Condition.Active);

                if (defaultStatus) {
                    this.filtersForm.setValue(
                        {
                            status: defaultStatus,
                            source: null
                        },
                        { emitEvent: false }
                    );

                    this.formValueSignal.set({
                        status: defaultStatus,
                        source: null
                    });

                    this.isInitialized.set(true);

                    const filters: ICustomizationFilters = {
                        status: defaultStatus,
                        source: undefined
                    };

                    this.previousFilters.set(filters);
                    this.filtersChange.emit(filters);
                }
            }
        });
    }

    private emitCurrentFilters(): void {
        if (!this.optionsLoaded()) return;

        if (!this.isInitialized()) return;

        const formValue = this.formValueSignal();
        if (!formValue) return;

        const filters: ICustomizationFilters = {
            status: formValue.status ?? undefined,
            source: formValue.source ?? undefined
        };

        if (!this.haveFiltersChanged(this.previousFilters(), filters)) {
            return;
        }

        this.previousFilters.set(filters);
        this.filtersChange.emit(filters);
    }

    private haveFiltersChanged(previous: ICustomizationFilters | null, current: ICustomizationFilters): boolean {
        if (!previous) return true;
        return previous.status?.id !== current.status?.id || previous.source?.id !== current.source?.id;
    }
}
